import NumberFlow from "@number-flow/react";
import { Link } from "@tanstack/react-router";
import {
  AuthConfig,
  cn,
  Control,
  ControlMonitorSummaryResponse,
  getControlCategoryName,
  getControlName,
  pluralize,
  queries,
  useAccountControls,
  useAuthConfig,
  ValidControlCategory,
} from "@theprelude/core";
import { ControlIcon, ShieldIcon } from "@theprelude/ds";
import { createQuery } from "@/lib/create-query";
import { getControlId, useExceptedFilters } from "@/queries/control-monitor";
import { Card, CardHeader, ListItem, Metric, MetricSet } from "../sections";
import { SummaryIconBubble, SummaryStatusIcon } from "../summary-icon-bubbles";

export const CategoryControlsCard = ({
  category,
}: {
  category: ValidControlCategory;
}) => {
  const authConfig = useAuthConfig();
  const { showExcepted } = useExceptedFilters();

  const { data: controls = [] } = useAccountControls();

  const {
    data: {
      controlFailuresCount,
      misconfigurationsCount,
      highestIssueControls,
    },
  } = summaryQuery({ authConfig, category, showExcepted }).useSuspenseQuery();

  return (
    <Card className="flex flex-col gap-2">
      <CardHeader>
        <SummaryIconBubble>
          <ShieldIcon />
        </SummaryIconBubble>
        {getControlCategoryName(category)} Controls
      </CardHeader>
      <div className="flex flex-col gap-4">
        <MetricSet>
          <div className="flex flex-col items-center gap-2">
            <Metric label="Control failures" count={controlFailuresCount} />
          </div>
          <div className="flex flex-col items-center gap-2">
            <Metric label="Misconfigurations" count={misconfigurationsCount} />
          </div>
        </MetricSet>
        <div className="flex flex-col">
          {highestIssueControls.map(
            ({ control, instanceId, name, issueCount }) => (
              <ListItem
                key={instanceId}
                asChild
                className="flex items-center gap-2"
              >
                <Link
                  to="/control-monitor"
                  search={{
                    active_id: getControlId({
                      controls,
                      control,
                      category,
                      instanceId,
                    }),
                  }}
                >
                  <ControlIcon
                    control={control}
                    className="text-secondary-foreground"
                  />
                  <div className="flex flex-1 items-baseline gap-2">
                    <span>{getControlName(control)}</span>
                    {name && (
                      <span className="text-xs text-secondary-foreground">
                        ({name})
                      </span>
                    )}
                  </div>
                  <div
                    className={cn(
                      "flex w-[120px] items-center gap-2 font-medium",
                      issueCount > 0 ? "text-status-red" : "text-status-green",
                    )}
                  >
                    <SummaryStatusIcon
                      success={issueCount === 0}
                      size="small"
                    />
                    <NumberFlow value={issueCount} />{" "}
                    {pluralize(issueCount, "issue")}
                  </div>
                </Link>
              </ListItem>
            ),
          )}
        </div>
      </div>
    </Card>
  );
};

const summaryQuery = ({
  authConfig,
  category,
  showExcepted,
}: {
  authConfig: AuthConfig;
  category: ValidControlCategory;
  showExcepted: boolean;
}) =>
  createQuery({
    queries: [
      queries.iam.getAccount(authConfig),
      queries.scm.summary(authConfig),
    ],
    queryFn: (account, summary) => ({
      controlFailuresCount: getControlFailuresCount(
        summary,
        category,
        showExcepted,
      ),
      misconfigurationsCount: getMisconfigurationsCount(
        summary,
        category,
        showExcepted,
      ),
      highestIssueControls: getHighestIssueControls(
        account.controls,
        summary,
        category,
        showExcepted,
      ),
    }),
  });

const getControlFailuresCount = (
  summary: ControlMonitorSummaryResponse,
  category: ValidControlCategory,
  showExcepted: boolean,
) => {
  return [summary.endpoint_summary, summary.user_summary]
    .flatMap((item) =>
      item.categories
        .filter((c) => c.category === category)
        .flatMap((c) =>
          c.instances.map(
            (instance) =>
              (showExcepted
                ? instance.control_failure_count
                : instance.excepted.control_failure_count) ?? 0,
          ),
        ),
    )
    .reduce((sum, current) => sum + current, 0);
};

const getMisconfigurationsCount = (
  summary: ControlMonitorSummaryResponse,
  category: ValidControlCategory,
  showExcepted: boolean,
) => {
  return [summary.endpoint_summary, summary.inbox_summary, summary.user_summary]
    .flatMap((item) =>
      item.categories
        .filter((c) => c.category === category)
        .flatMap((category) =>
          category.instances.map(
            (instance) =>
              (showExcepted
                ? instance.setting_misconfiguration_count
                : instance.excepted.setting_misconfiguration_count) ?? 0,
          ),
        ),
    )
    .reduce((sum, current) => sum + current, 0);
};

const getHighestIssueControls = (
  controls: Control[],
  summary: ControlMonitorSummaryResponse,
  category: ValidControlCategory,
  showExcepted: boolean,
) => {
  return [
    ...summary.endpoint_summary.categories,
    ...summary.inbox_summary.categories,
    ...summary.user_summary.categories,
  ]
    .filter((c) => c.category === category)
    .flatMap((item) =>
      item.instances.map((instance) => {
        const name = (
          controls.find((c) => c.instance_id === instance.instance_id)?.name ??
          ""
        ).trim();

        const keys = Object.keys(
          instance.excepted,
        ) as (keyof typeof instance.excepted)[];

        const issueCount = keys.reduce((sum: number, key) => {
          const value = showExcepted ? instance[key] : instance.excepted[key];
          return sum + (value ?? 0);
        }, 0);

        return {
          control: instance.control,
          instanceId: instance.instance_id,
          name,
          issueCount,
        };
      }),
    )
    .sort((a, b) => (a.issueCount > b.issueCount ? -1 : 1))
    .slice(0, 3);
};
